import { createBarcodeScanDetector } from '@/lib/barcode-scan'
import { isKeyForFocusedControl } from '@/lib/page-keyboard'
import { type KeyboardEvent, useEffect, useEffectEvent, useState } from 'react'

/**
 * Hears a barcode scanner anywhere on the page, so florists can scan without clicking into a field.
 * Typing in a field, or anything while a dialog is open, is left alone.
 */
export function usePageBarcodeScanner({
  enabled = true,
  onScan,
}: {
  enabled?: boolean
  onScan: (code: string) => void
}): void {
  const handleScan = useEffectEvent(onScan)

  useEffect(() => {
    if (!enabled) {
      return
    }

    const detector = createBarcodeScanDetector()
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (isKeyForFocusedControl(event)) {
        detector.reset()
        return
      }

      // A space inside a code (FS 102), or the Enter that ends it, would press whichever button
      // has focus.
      const isSpaceInScan = event.key === ' ' && detector.continuesScan(event)

      if (detector.handleKey(event, (code) => handleScan(code)) || isSpaceInScan) {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      detector.reset()
    }
  }, [enabled])
}

/**
 * Tells a scan from typing in one text field. Returns the field's keydown handler, which stops the
 * Enter or Tab that ends a scan. A scan that ends on a pause reports after the last keydown has
 * rendered, so `onScan` should update state with functional updates.
 */
export function useInputBarcodeScanner(
  onScan: (code: string) => void
): (event: KeyboardEvent<HTMLInputElement>) => void {
  const [detector] = useState(createBarcodeScanDetector)

  // A scan waiting on its pause shouldn't report after the field is gone.
  useEffect(() => () => detector.reset(), [detector])

  return (event) => {
    if (detector.handleKey(event.nativeEvent, onScan)) {
      event.preventDefault()
    }
  }
}
